import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SEO from './SEO';
import './ForgotPassword.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.defaults.withCredentials = true;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '' });
  const [touched, setTouched] = useState({ email: false });
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Validation function
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      return 'Email address is required';
    }
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(emailValue)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  // Handle field blur
  const handleBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    setFieldErrors(prev => ({ ...prev, email: validateEmail(email) }));
  };

  // Handle email change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  // Redirect authenticated users away from forgot-password page
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/research');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-card">
            <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if user is authenticated (will redirect via useEffect)
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Mark field as touched
    setTouched({ email: true });

    // Validate email
    const emailError = validateEmail(email);
    setFieldErrors({ email: emailError });

    // If there are validation errors, don't submit
    if (emailError) {
      return;
    }

    setLoading(true);
    setSubmitted(false); // Ensure submitted is false when starting a new request

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      // Only set submitted to true if we get a successful response
      // Check if the response indicates an error (even if status is 200)
      if (response.data && response.data.message && 
          (response.data.message.includes('Google Sign-In') || response.data.message.includes('Google Sign'))) {
        // This shouldn't happen (should be caught in catch), but handle it just in case
        setError(response.data.message);
        setFieldErrors({ email: '' }); // Clear field error to avoid duplication
        setSubmitted(false);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      // Ensure submitted is false on error
      setSubmitted(false);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to send reset email. Please try again.';
      
      // Check if this is a Google OAuth user error
      if (errorMessage.includes('Google Sign-In') || errorMessage.includes('Google Sign')) {
        // For Google OAuth errors, show as general error message (more prominent)
        setError(errorMessage);
        setFieldErrors({ email: '' }); // Clear field error to avoid duplication
      } else {
        setError(errorMessage);
        // Clear field errors for non-email errors
        setFieldErrors({ email: '' });
        // If it's an email-related error, show it in the field
        if (errorMessage.toLowerCase().includes('email') || errorMessage.toLowerCase().includes('invalid')) {
          setFieldErrors({ email: errorMessage });
          setError(''); // Clear general error to avoid duplication
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <SEO title="Reset Password" path="/forgot-password" description="Reset your LawFusion account password." noIndex={true} />
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          {!submitted ? (
            <>
              {/* Welcome Message */}
              <h1 className="forgot-password-title">Reset Your Password</h1>
              <p className="forgot-password-tagline">Enter your email address and we'll send you a link to reset your password.</p>

              {/* Forgot Password Form */}
              <form onSubmit={handleSubmit} className="forgot-password-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleBlur}
                    required
                    placeholder="you@firm.com"
                    className={touched.email && fieldErrors.email ? 'input-error' : ''}
                  />
                  {touched.email && fieldErrors.email && (
                    <div className="field-error">
                      {fieldErrors.email}
                    </div>
                  )}
                </div>

                {error && <div className="error-message">{error}</div>}

                {/* Submit Button */}
                <button type="submit" className="reset-button" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              {/* Back to Login Link */}
              <p className="back-to-login">
                Remember your password? <Link to="/login">Sign in</Link>
              </p>
            </>
          ) : (
            <div className="email-sent-screen">
              {/* Animated envelope icon */}
              <div className="email-sent-icon-wrap">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#2E5BFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 4L12 13L2 4" />
                </svg>
              </div>

              <h2 className="email-sent-title">Check Your Email</h2>

              <p className="email-sent-subtitle">
                We've sent a password reset link to
              </p>
              <div className="email-sent-address">
                {email}
              </div>

              <div className="email-sent-steps">
                <div className="email-sent-step">
                  <span className="email-sent-step-num">1</span>
                  <span>Open the email from <strong>LawFusion</strong></span>
                </div>
                <div className="email-sent-step">
                  <span className="email-sent-step-num">2</span>
                  <span>Click the <strong>Reset Password</strong> button</span>
                </div>
                <div className="email-sent-step">
                  <span className="email-sent-step-num">3</span>
                  <span>Create your new password</span>
                </div>
              </div>

              <div className="email-sent-expiry">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>This link expires in <strong>1 hour</strong></span>
              </div>

              <p className="email-sent-spam-note">
                Don't see it? Check your spam or junk folder.
              </p>

              <button
                className="reset-button"
                onClick={() => { setSubmitted(false); setError(''); }}
                style={{ marginTop: '0.5rem' }}
              >
                Try a different email
              </button>

              <p className="back-to-login" style={{ marginTop: '0.75rem' }}>
                <Link to="/login">Back to Sign in</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

